import type { ReactNode } from 'react';

import { Input } from '@/components/form/Input';
import type {
  SessionSource,
  StepProps,
} from '@/sections/asset/components/tabs/credentials/RecipeWizard/RecipeWizard.types';
import { SESSION_STRATEGIES } from '@/sections/asset/components/tabs/credentials/RecipeWizard/wizardConfig';

import { RadioCard } from './RadioCard';
import { SectionIntro } from './shared';

export function SessionStep({ data, setData }: StepProps) {
  const select = (source: SessionSource) =>
    setData(prev => ({ ...prev, sessionSource: source }));

  return (
    <div className="space-y-5">
      <SectionIntro
        title="Where does your app put the session after login?"
        body={
          <p>
            Pick where the login response delivers the session. We&apos;ll grab
            that value and replay it on every scan.
          </p>
        }
      />

      {(Object.keys(SESSION_STRATEGIES) as SessionSource[]).map(source => (
        <RadioCard
          key={source}
          selected={data.sessionSource === source}
          onSelect={() => select(source)}
          title={SESSION_STRATEGIES[source].label}
          description={SESSION_STRATEGIES[source].description}
          testId={`wizard-session-${source}`}
        >
          {data.sessionSource === source && (
            <StrategyFields source={source} data={data} setData={setData} />
          )}
        </RadioCard>
      ))}
    </div>
  );
}

// StrategyFields renders the form inputs specific to one session source.
// Kept as a switch because each source has heterogeneous fields (cookie has
// one input, body has three). Labels/descriptions and the toSteps projection
// live centrally in SESSION_STRATEGIES; only this UI is per-source.
function StrategyFields({
  source,
  data,
  setData,
}: StepProps & { source: SessionSource }): ReactNode {
  if (source === 'cookie') {
    return (
      <Input
        label="Cookie name"
        name="wizard-cookie-name"
        value={data.cookieName}
        onChange={e =>
          setData(prev => ({ ...prev, cookieName: e.target.value }))
        }
        placeholder="session"
        required
        info={{
          text: 'Common names: session, sessionid, JSESSIONID, connect.sid, PHPSESSID.',
        }}
        data-testid="wizard-cookie-name"
      />
    );
  }
  if (source === 'response_header') {
    return (
      <Input
        label="Response header name"
        name="wizard-header-name"
        value={data.headerName}
        onChange={e =>
          setData(prev => ({ ...prev, headerName: e.target.value }))
        }
        placeholder="Authorization"
        required
        data-testid="wizard-header-name"
      />
    );
  }
  return (
    <div className="space-y-3">
      <Input
        label="JSONPath to the token"
        name="wizard-json-path"
        value={data.jsonPath}
        onChange={e => setData(prev => ({ ...prev, jsonPath: e.target.value }))}
        placeholder="data.access_token"
        required
        info={{
          text: 'Dotted path. For {"data":{"token":"abc"}} use data.token.',
        }}
        data-testid="wizard-json-path"
      />
      <Input
        label="Header scanners will send"
        name="wizard-output-header"
        value={data.bodyOutputHeader}
        onChange={e =>
          setData(prev => ({ ...prev, bodyOutputHeader: e.target.value }))
        }
        placeholder="Authorization"
        required
        data-testid="wizard-output-header"
      />
      <Input
        label="Header value template"
        name="wizard-output-value"
        value={data.bodyOutputValue}
        onChange={e =>
          setData(prev => ({ ...prev, bodyOutputValue: e.target.value }))
        }
        placeholder="Bearer {{token}}"
        required
        info={{
          text: 'Use {{token}} where the extracted value should go. Most APIs want "Bearer {{token}}".',
        }}
        data-testid="wizard-output-value"
      />
    </div>
  );
}
